import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SortingState } from "@tanstack/react-table";
import DataTable from "@/components/controls/DataTable/DataTable";
import { Schedule as ScheduleIcon } from "@dreamhub/designsystem/icons";
import {
  ReportsTableModal,
  REPORTS_TABLE_CLASSNAME,
  REPORTS_TABLE_HEADER_CLASSNAME
} from "@/components/global/Chat/components/dreamerReports/ReportsTableModal";
import SearchButtonInput from "@/components/ui/search-button-input";
import ScheduleModal from "@/components/global/Chat/components/ScheduleModal/ScheduleModal";
import RemoveScheduleConfirmationModal from "@/components/global/Chat/components/ScheduleModal/RemoveScheduleConfirmationModal";
import { ReportPreviewHost } from "@/components/global/Chat/components/dreamerReports/ReportPreviewHost";
import { useRecurringReportColumns } from "@/components/global/Chat/components/dreamerReports/useRecurringReportColumns";
import { useScheduleConfirmation } from "@/components/global/Chat/components/dreamerReports/useScheduleConfirmation";
import {
  useDeleteReportingScheduler,
  useReportingSchedulersFilter
} from "@/entities/reportingScheduler/hooks";
import { ReportingScheduler } from "@/entities/reportingScheduler/types";
import { ReportPreviewTarget } from "@/entities/savedReports/types";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import { useReportRuns } from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import {
  RECURRING_REPORTS_TRANSLATION_KEY,
  TRANSLATION_KEY
} from "@/components/global/Chat/components/dreamerReports/consts";

interface RecurringReportsModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const RecurringReportsModal: React.FC<RecurringReportsModalProps> = ({
  isOpen,
  setIsOpen
}) => {
  const { t } = useTranslation();
  const appendConfirmation = useScheduleConfirmation();

  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [editTarget, setEditTarget] = useState<ReportingScheduler | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<ReportingScheduler | null>(
    null
  );
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [lastReportTarget, setLastReportTarget] =
    useState<ReportPreviewTarget | null>(null);

  // This component outlives the dialog, so a search left in place would hide
  // schedules the next time the modal opens.
  useEffect(() => {
    if (!isOpen) setSearchTerm("");
  }, [isOpen]);

  const sortField = sorting[0]?.id;
  const sortAsc = sorting[0] ? !sorting[0].desc : undefined;

  const {
    data: schedules = [],
    isLoading,
    refetch: refetchSchedules
  } = useReportingSchedulersFilter(isOpen, searchTerm, sortField, sortAsc);
  const deleteScheduler = useDeleteReportingScheduler();
  const { forgetSavedReport } = useChat();
  const { forgetReportRun } = useReportRuns();

  const untitledLabel = t(`${TRANSLATION_KEY}.untitled`);

  const handleEdit = useCallback((schedule: ReportingScheduler) => {
    setEditTarget(schedule);
    setIsEditOpen(true);
  }, []);

  const handleCancel = useCallback((schedule: ReportingScheduler) => {
    setRemoveTarget(schedule);
    setIsRemoveConfirmOpen(true);
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (!removeTarget) return;
    const schedule = removeTarget;
    deleteScheduler.mutate(
      { pathParams: { id: schedule.id } },
      {
        onSuccess: () => {
          // The row is gone, so no message may keep pointing a save at it.
          forgetSavedReport(schedule.id);
          forgetReportRun(schedule.id);
          appendConfirmation({
            kind: "canceled",
            name: schedule.name || untitledLabel
          });
        }
      }
    );
  }, [
    deleteScheduler,
    forgetSavedReport,
    forgetReportRun,
    appendConfirmation,
    untitledLabel,
    removeTarget
  ]);

  const handleOpenLastReport = useCallback((schedule: ReportingScheduler) => {
    // The delivery is addressed by its attachment, not by its message: a
    // schedule's last fire is routinely older than the window the chat socket
    // carries, so looking the message up among the loaded ones found nothing
    // and the button did nothing. The host resolves the attachment itself, and
    // says so when it cannot instead of failing quietly.
    setLastReportTarget({
      id: schedule.id,
      name: schedule.name,
      // Scheduling inside the preview addresses the message the report was
      // saved from, even when a later delivery is what is on screen.
      originMessageId: schedule.messageId,
      originAttachmentId: schedule.lastReportAttachmentId ?? "",
      // Every row on this surface is a recurring schedule by definition.
      isScheduled: true
    });
  }, []);

  const columns = useRecurringReportColumns({
    t,
    untitledLabel,
    cancelPending: deleteScheduler.isPending,
    onEdit: handleEdit,
    onCancel: handleCancel,
    onOpenLastReport: handleOpenLastReport
  });

  const hasActiveSearch = searchTerm.trim().length > 0;
  const hasNoResults = !isLoading && schedules.length === 0;
  const isEmptyState = hasNoResults && !hasActiveSearch;
  const isNoSearchResults = hasNoResults && hasActiveSearch;

  const toolbar = (
    <div
      data-test="recurring-reports-toolbar"
      className="flex gap-2 border-b border-border-border1 justify-between shrink-0 px-4"
    >
      <div className="flex items-center gap-[6px] flex-wrap">
        {!isEmptyState && (
          <SearchButtonInput
            placeholder={t("search")}
            inputValue={searchTerm}
            setInputValue={setSearchTerm}
            searchIconColor="secondary"
            tooltipText={t("search")}
            iconButtonSize="lg"
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <ReportsTableModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        titleIcon={<ScheduleIcon />}
        title={t(`${RECURRING_REPORTS_TRANSLATION_KEY}.title`)}
        toolbar={toolbar}
        emptyText={
          hasNoResults
            ? t(
                `${RECURRING_REPORTS_TRANSLATION_KEY}.${isNoSearchResults ? "noSearchResults" : "empty"}`
              )
            : undefined
        }
        table={
          <DataTable
            dataTest="recurring-reports-table"
            columns={columns}
            data={schedules}
            isDataLoading={isLoading}
            manualSorting
            initialSortBy={sorting}
            onSortingChange={setSorting}
            headerClassName={REPORTS_TABLE_HEADER_CLASSNAME}
            className={REPORTS_TABLE_CLASSNAME}
          />
        }
        dataTest="recurring-reports-modal"
      />
      {editTarget && (
        <ScheduleModal
          isOpen={isEditOpen}
          setIsOpen={setIsEditOpen}
          messageId={editTarget.messageId}
          existingScheduleData={editTarget}
          onSaved={() => refetchSchedules()}
        />
      )}
      <RemoveScheduleConfirmationModal
        isOpen={isRemoveConfirmOpen}
        setIsOpen={setIsRemoveConfirmOpen}
        onConfirm={handleConfirmRemove}
      />
      <ReportPreviewHost
        report={lastReportTarget}
        onClose={() => setLastReportTarget(null)}
      />
    </>
  );
};

export default RecurringReportsModal;
